'use client';

import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Field, FieldProps } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import 'intl-tel-input/build/css/intlTelInput.css';

interface PhoneFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
}

const formFieldStyles = tv({
  base: [
    'w-full',
    'min-h-12',
    'px-spacing-spacing-16',
    'py-spacing-spacing-12',
    'border',
    'border-color-input-border',
    'rounded-[8px]',
    'bg-color-grayscale-white',
    'placeholder:text-color-text-text-secondary',
    'text-color-text-text',
    'hover:border-color-input-border-hover',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'placeholder:italic',
    'font-normal',
    'disabled:text-color-text-text-disabled',
    'disabled:cursor-not-allowed',
    'focus:outline-none',
    'focus:ring-1',
    'focus:border-color-input-border-focused',
    'focus:ring-color-input-border-focused',
    'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
    'intl-tel-input',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus:border-color-text-text-error',
        'bg-color-fill-error-light',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

type IntlTelInputInstance = {
  setNumber: (number: string) => void;
  getValidationError: () => number;
};

type IntlTelInputRef = {
  getInstance: () => IntlTelInputInstance | null;
};

/** libphonenumber validationError codes used for over-length checks */
export const PHONE_VALIDATION_ERROR = {
  TOO_LONG: 3,
  INVALID_LENGTH: 5,
} as const;

export const isPhoneNumberOverLimit = (errorCode: number | null | undefined): boolean =>
  errorCode === PHONE_VALIDATION_ERROR.TOO_LONG ||
  errorCode === PHONE_VALIDATION_ERROR.INVALID_LENGTH;

/**
 * Custom check: national number is only zeros (e.g. 0000000000 → +440000000000).
 * Country calling code (1–3 digits) is ignored; empty values are ignored.
 */
export const isAllZeroPhoneNumber = (value: string): boolean => {
  if (!value?.trim()) {
    return false;
  }

  const digits = value.replace(/\D/g, '');
  if (digits.length < 9) {
    return false;
  }

  // Entire value is zeros (no / incomplete country code)
  if (/^0+$/.test(digits)) {
    return true;
  }

  // Strip 1–3 digit country code; treat remaining national digits as the subscriber number
  for (let countryCodeLength = 1; countryCodeLength <= 3; countryCodeLength++) {
    const nationalNumber = digits.slice(countryCodeLength);
    if (nationalNumber.length >= 9 && /^0+$/.test(nationalNumber)) {
      return true;
    }
  }

  return false;
};

// Custom Formik Field component for intl-tel-input React component
const CustomPhoneField: React.FC<
  FieldProps & {
    placeholder?: string;
    isDisabled?: boolean;
    hasError?: boolean;
    defaultCountry?: string;
    'aria-required'?: boolean;
    itiRef: React.MutableRefObject<IntlTelInputRef | null>;
  }
> = ({
  field,
  form,
  placeholder,
  isDisabled = false,
  hasError = false,
  defaultCountry = 'gb',
  'aria-required': ariaRequired = false,
  itiRef,
}) => {
  const fieldState = isDisabled ? 'disabled' : hasError ? 'error' : 'default';
  const [IntlTelInputComponent, setIntlTelInputComponent] =
    useState<React.ForwardRefExoticComponent<
      Record<string, unknown> & React.RefAttributes<IntlTelInputRef>
    > | null>(null);

  // Use refs to store the latest values to avoid stale closures
  const fieldRef = useRef(field);
  const formRef = useRef(form);
  const previousCountryRef = useRef<string | null>(null);

  // Update refs when props change
  useEffect(() => {
    fieldRef.current = field;
    formRef.current = form;
  });

  useEffect(() => {
    let active = true;
    void import('intl-tel-input/reactWithUtils').then((mod) => {
      if (active) {
        setIntlTelInputComponent(
          () =>
            mod.default as React.ForwardRefExoticComponent<
              Record<string, unknown> & React.RefAttributes<IntlTelInputRef>
            >
        );
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const handleChangeNumber = useCallback(
    (number: string) => {
      // Remove spaces from the phone number before storing in Formik
      const cleanNumber = number.replace(/\s/g, '');

      // Only update if the value has actually changed to prevent infinite loops
      if (fieldRef.current.value !== cleanNumber) {
        const fieldName = fieldRef.current.name;
        // Re-validate so invalid numbers show an error while typing
        formRef.current.setFieldValue(fieldName, cleanNumber, true);
        // Errors only render when touched — mark touched for zeros / over-limit
        const errorCode = itiRef.current?.getInstance()?.getValidationError();
        if (isAllZeroPhoneNumber(cleanNumber) || isPhoneNumberOverLimit(errorCode)) {
          formRef.current.setFieldTouched(fieldName, true, false);
        }
      }
    },
    [itiRef]
  );

  // Must be a stable callback — an inline handler re-creates on every render and
  const handleChangeCountry = useCallback(
    (country: string) => {
      const previousCountry = previousCountryRef.current;
      previousCountryRef.current = country;

      // Skip initial country selection so mount/UK format setup does not clear/re-render
      if (previousCountry === null || previousCountry === country) {
        return;
      }

      //  clear the input instance and Formik value
      itiRef.current?.getInstance()?.setNumber('');
      formRef.current.setFieldValue(fieldRef.current.name, '', true);
    },
    [itiRef]
  );

  const handleBlur = useCallback(() => {
    // Mark field as touched and trigger validation
    formRef.current.setFieldTouched(fieldRef.current.name, true, true);
  }, []);

  return (
    <div className="relative w-full flex flex-col">
      {!IntlTelInputComponent ? (
        <input
          id={field.name}
          name={field.name}
          className={formFieldStyles({ state: fieldState })}
          placeholder={placeholder}
          disabled={isDisabled}
          onBlur={handleBlur}
          aria-required={ariaRequired}
          defaultValue={field.value || ''}
        />
      ) : (
        <IntlTelInputComponent
          key={field.name} // Stable key to prevent re-initialization
          ref={itiRef}
          initialValue={field.value || ''}
          onChangeNumber={handleChangeNumber}
          onChangeCountry={handleChangeCountry}
          disabled={isDisabled}
          initOptions={{
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            initialCountry: (defaultCountry as any) || 'gb', // Cast to avoid strict typing
            separateDialCode: true,
            strictMode: true,
            useFullscreenPopup: false,
            autoPlaceholder: 'aggressive',
          }}
          inputProps={
            {
              name: field.name,
              id: field.name,
              className: formFieldStyles({ state: fieldState }),
              placeholder: placeholder,
              onBlur: handleBlur,
              'aria-required': ariaRequired,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          } // Cast to avoid TypeScript issues
        />
      )}
      {/* Style Country Flag */}
      <style jsx>{`
        :global(.iti__search-input-wrapper) {
          padding: var(--spacing-spacing-16);
        }
        :global(.iti__search-icon) {
          left: 23px;
          padding: 1px;
        }
        :global(.iti__search-icon-svg) {
          width: 21px;
          height: 21px;
          stroke-width: 2;
          stroke: #000;
        }
        :global(.iti__search-clear) {
          right: 30px;
        }
        :global(.iti__search-input) {
          padding-left: var(--spacing-spacing-32);
          padding-right: var(--spacing-spacing-40);
          padding-top: var(--spacing-spacing-12);
          padding-bottom: var(--spacing-spacing-12);
          border-radius: var(--general-border-radius-text-input);
          border: 1px solid var(--color-input-border);
          background-color: var(--color-input-bg-surface);
          color: var(--color-text-text);
          font-weight: 400;
          font-family: var(--typography-font-family-body);
          font-size: var(--typography-font-size-body-medium);
        }
        :global(.iti__search-input::placeholder) {
          color: var(--color-text-text-secondary);
          font-style: italic;
        }
        :global(.iti__country) {
          margin-left: 10px !important;
          margin-right: 10px !important;
          gap: var(--spacing-spacing-8);
          border-radius: var(--border-radius-radius-1);
        }
        :global(.iti__country-list .iti__flag) {
          margin-right: 0;
        }
        :global(.iti__country-name) {
          margin-right: 0;
          color: var(--component-button-tertiary-text);
          font-family: var(--typography-font-family-body);
          font-size: var(--typography-font-size-body-medium);
          font-style: normal;
          font-weight: 600;
          line-height: var(--typography-line-height-xxsmall);
        }
        :global(.iti__dial-code) {
          color: var(--color-text-text-secondary);
          font-family: var(--typography-font-family-body);
          font-size: var(--typography-font-size-body-medium);
          font-style: normal;
          font-weight: 400;
          line-height: var(--typography-line-height-body-medium);
        }
        :global(
          .iti--allow-dropdown
            .iti__country-container:not(:has(+ input[disabled])):not(:has(+ input[readonly]))
            .iti__selected-country-primary:hover
        ) {
          background-color: transparent;
        }
        :global(
          .iti--allow-dropdown
            .iti__country-container:not(:has(+ input[disabled])):not(:has(+ input[readonly]))
            .iti__selected-country:has(+ .iti__dropdown-content:hover)
            .iti__selected-country-primary
        ) {
          background-color: transparent;
        }
        :global(.iti--inline-dropdown .iti__dropdown-content) {
          margin-top: -6px;
          margin-bottom: 0;
          border-radius: 0 0 var(--border-radius-radius-2) var(--border-radius-radius-2);
          border-right: 1px solid var(--color-input-border-hover);
          border-bottom: 1px solid var(--color-input-border-hover);
          border-left: 1px solid var(--color-input-border-hover);
          box-shadow: none;
          border-top: 0;
        }
        :global(.iti__country.iti__highlight) {
          background-color: var(--color-core-color-4-skyblue-lightTint);
        }
        :global(.iti__selected-country-primary) {
          padding: var(--spacing-spacing-12) var(--spacing-spacing-4) var(--spacing-spacing-12)
            var(--spacing-spacing-12);
          gap: var(--spacing-spacing-2);
        }
        :global(.iti__arrow) {
          border-top-width: 6px;
          border-right-width: 6px;
          border-left-width: 6px;
          border-top-color: var(--component-button-tertiary-icon);
          margin: 6px;
        }
        :global(.iti__arrow--up) {
          border-bottom-width: 6px;
          border-bottom-color: var(--component-button-tertiary-icon);
        }
      `}</style>
    </div>
  );
};

export const PhoneField: React.FC<PhoneFieldProps> = ({ field, hasError = false }) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const placeholder = fieldConfig?.placeholderText?.value;
  const isRequired = fieldConfig?.required?.value || false;
  const isDisabled = fieldConfig?.disabled?.value || false;
  const defaultCountry = fieldConfig?.phoneDefaultCountryCode?.value || 'gb';
  const validationErrorMessage =
    fieldConfig?.validationErrorMessage?.value || 'Please enter a valid phone number';
  const itiRef = useRef<IntlTelInputRef | null>(null);

  const validatePhone = useCallback(
    (value: string) => {
      if (!value) {
        return undefined;
      }

      if (isAllZeroPhoneNumber(value)) {
        return validationErrorMessage;
      }

      // Country max length exceeded (e.g. leading zeros bypass strictMode digit cap)
      const errorCode = itiRef.current?.getInstance()?.getValidationError();
      if (isPhoneNumberOverLimit(errorCode)) {
        return validationErrorMessage;
      }

      return undefined;
    },
    [validationErrorMessage]
  );

  if (!fieldName) {
    return null;
  }

  return (
    <FormFieldWrapper
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isRequired={isRequired}
      hasError={hasError}
    >
      <Field name={fieldName} validate={validatePhone}>
        {(fieldProps: FieldProps) => (
          <CustomPhoneField
            {...fieldProps}
            placeholder={placeholder}
            isDisabled={isDisabled}
            hasError={hasError}
            defaultCountry={defaultCountry}
            aria-required={isRequired}
            itiRef={itiRef}
          />
        )}
      </Field>
    </FormFieldWrapper>
  );
};
